import {AxiosError} from "axios";
import {DEFAULT_PORTAL_FORGOT_PASSWORD_KEYS, PortalForgotPasswordTranslationKeys} from "components/src/auth/PortalTranslationKeys";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from "dompurify";
import {useEffect, useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from 'react-bootstrap/Form';

interface PortalForgotPasswordProps {
    /**
     * Starts the password reset for the given email. Provided by the consuming app so this
     * component can be reused with different back-end methods.
     */
    forgotPassword: (email: string) => Promise<unknown>;
    /**
     * Overrides for the i18next keys. Only the keys that need to differ have to be provided;
     * the rest fall back to {@link DEFAULT_PORTAL_FORGOT_PASSWORD_KEYS}.
     */
    translationKeys?: Partial<PortalForgotPasswordTranslationKeys>;
}

export const PortalForgotPassword = ({forgotPassword, translationKeys}: PortalForgotPasswordProps) =>
{
    const keys = {...DEFAULT_PORTAL_FORGOT_PASSWORD_KEYS, ...translationKeys};

    /**
     * Email address
     */
    const [email, setEmail] = useState<string>();
    /**
     * true if a reset was sent
     */
    const [resetSent, setResetSent] = useState<boolean>(false);
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Ref for email input field
     */
    const emailInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const sanitizeHTML = (html : string) => {
        return {__html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })};
    }

    useEffect(() => {
        emailInputRef.current?.focus();
    }, []);

    const startPasswordReset = () =>
    {
        if (email === undefined) {
            throw new Error("email is undefined");
        }

        setErrorMessage(undefined);

        forgotPassword(email)
            .then(() =>
            {
                setResetSent(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error starting password reset", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
    }

    return (
        <>
        {t([keys.header]).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t([keys.header]))} />}
        <div className="w-100 p-4 shadow-sm rounded bg-body">
            <div className="mb-4 text-center">
                <h2>{t(keys.title)}</h2>
            </div>

            {/* show an alert if a validation error occurred */}
            <RenderAlert message={errorMessage}/>

            {resetSent && <RenderAlert message={t(keys.resetSent)} variant="success"/>}

            <FloatingLabel label={t(keys.emailLabel)} className="text-secondary mb-3">
                <Form.Control type="email" value={email} placeholder="Email"
                              ref={emailInputRef}
                              onChange={e => {setEmail(e.target.value); setResetSent(false)}}/>
            </FloatingLabel>

            {t(keys.customText).length > 0 &&
                <div className="mt-2" dangerouslySetInnerHTML={sanitizeHTML(t([keys.customText]))} />
            }

            <Button variant="success" className="w-100 mt-4" disabled={StringUtils.isEmpty(email) || resetSent}
                    onClick={startPasswordReset}>
                {t(keys.resetButton)}
            </Button>
        </div>
        {t([keys.footer]).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t([keys.footer]))} />}
        </>
    );
}
